import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Live Gratis-Versand-Anzeige auf der Produktseite.
 *
 * @typedef {Object} FreeShippingBarRefs
 * @property {HTMLElement} container
 * @property {HTMLElement} title
 * @property {HTMLElement} detail
 * @property {HTMLElement} fill
 * @property {HTMLElement} badge
 *
 * @extends {Component<FreeShippingBarRefs>}
 */
class FreeShippingBarComponent extends Component {
  requiredRefs = ['container', 'title', 'detail', 'fill', 'badge'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.#syncFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        if (typeof cart?.total_price === 'number') {
          this.#render(cart.total_price);
          return;
        }

        this.#syncFromCart();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          this.#syncFromCart();
        }
      });
  };

  async #syncFromCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const cart = await response.json();
      if (typeof cart?.total_price === 'number') {
        this.#render(cart.total_price);
      }
    } catch {
      // no-op
    }
  }

  /**
   * @param {number} totalPrice
   */
  #render(totalPrice) {
    const threshold = Number(this.dataset.threshold) || 10000;
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';

    const remaining = Math.max(threshold - totalPrice, 0);
    const progress = Math.min(Math.max((totalPrice / threshold) * 100, 0), 100);
    const unlocked = totalPrice >= threshold && totalPrice > 0;

    const thresholdMoney = formatMoney(threshold, moneyFormat, currency);
    const remainingMoney = formatMoney(remaining, moneyFormat, currency);

    /** @type {string} */
    let title;
    /** @type {string} */
    let detail;

    if (unlocked) {
      title = 'Du hast kostenlosen Versand!';
      detail = 'Versandkosten entfallen an der Kasse.';
    } else if (totalPrice > 0) {
      title = `Noch ${remainingMoney} bis kostenloser Versand`;
      detail = `Ab ${thresholdMoney} Bestellwert versenden wir gratis.`;
    } else {
      title = `Kostenloser Versand ab ${thresholdMoney}`;
      detail = `Versandkosten werden erst an der Kasse berechnet – ab ${thresholdMoney} gratis.`;
    }

    this.refs.title.textContent = title;
    this.refs.detail.textContent = detail;
    this.refs.fill.style.setProperty('--bb-free-shipping-progress', `${progress}%`);
    this.refs.container.classList.toggle('bb-free-shipping--unlocked', unlocked);
    this.refs.badge.classList.toggle('hidden', !unlocked);
  }
}

if (!customElements.get('free-shipping-bar-component')) {
  customElements.define('free-shipping-bar-component', FreeShippingBarComponent);
}
